import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, Grid } from '@material-ui/core';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import locale from 'date-fns/locale/en-US';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Button, Label } from '@ipc/ipc-components';
import { StyledModalForm } from '../../../../../common/list-table/helpers/formModal.styles';
import DateFormats from '../../../../../../elements/DateFormats';
import { formatMessage } from '../../../../../../locales/locales';

function DeactivateDenestImpcMappingDialog(props) {
  const {
    closeDialog,
    openDialog,
    selectedRecords,
    confirmDeactivate,
    tableData,
  } = props;
  const [validTo, setValidTo] = useState(moment());
  const [isFormValid, setFormValid] = useState(true);
  const [overlappingDenest, setOverlappingDenest] = useState(null);

  const checkFormValidity = () => {
    const selectedActiveRecords = selectedRecords.filter((item) => moment(item.validFrom).isSameOrBefore(moment().startOf('day')));
    const selectedFutureRecords = selectedRecords.filter((item) => moment(item.validFrom).isAfter(moment().startOf('day')));
    const isDuplicateMappingSelected = selectedActiveRecords.find(
      (record) => !!selectedActiveRecords.find(
        (item) => item.mrdImpc === record.mrdImpc && item.denestImpc === record.denestImpc && record.rowNumber !== item.rowNumber,
      ),
    );
    if (isDuplicateMappingSelected) {
      return isDuplicateMappingSelected;
    }
    const notSelectedData = tableData.filter((item) => !selectedRecords.find((selectedRecord) => item.rowNumber === selectedRecord.rowNumber));
    const potentialOverlaps = notSelectedData.filter((record) => {
      const isRecordRelated = selectedActiveRecords.find((item) => (item.mrdImpc === record.mrdImpc && item.denestImpc === record.denestImpc && (record.validTo
        ? moment(record.validTo).isSameOrAfter(moment().subtract(1, 'day')) : true)));
      return !!isRecordRelated;
    });
    return potentialOverlaps.find((tableRecord) => {
      const relatedRecords = selectedActiveRecords.filter((item) => (!selectedFutureRecords.includes(tableRecord)
        && (item.mrdImpc === tableRecord.mrdImpc && item.denestImpc === tableRecord.denestImpc && !tableRecord.willDeleted)));
      return !!relatedRecords.find((relatedRecord) => {
        if (tableRecord.validTo) {
          return (moment(relatedRecord.validFrom).isBetween(moment(tableRecord.validFrom).subtract(1, 'day'), moment(tableRecord.validTo).add(1, 'day'))
            || validTo.isBetween(moment(tableRecord.validFrom).subtract(1, 'day'), moment(tableRecord.validTo).add(1, 'day')))
          || (validTo.isAfter(moment(tableRecord.validFrom)) && validTo.isAfter(moment(tableRecord.validTo))
              && moment(relatedRecord.validFrom).isBefore(moment(tableRecord.validFrom)) && moment(relatedRecord.validTo).isBefore(moment(tableRecord.validTo)));
        }
        return (moment(relatedRecord.validFrom).isBetween(moment(tableRecord.validFrom), moment(validTo.format('YYYY-MM-DD'))) || validTo.isSameOrAfter(moment(tableRecord.validFrom)));
      });
    });
  };

  useEffect(() => {
    const multipleSelectionValue = moment();
    const dateValue = selectedRecords.length === 1 && selectedRecords[0].validTo
      ? moment(selectedRecords[0].validTo) : multipleSelectionValue;
    setValidTo(dateValue);
  }, [selectedRecords, openDialog]);

  useEffect(() => {
    const minimumValidDate = moment().startOf('day');
    const formValidationResult = checkFormValidity();
    setOverlappingDenest(formValidationResult?.denestImpc || null);
    setFormValid(!formValidationResult && validTo.isSameOrAfter(minimumValidDate));
  }, [validTo]);

  const onChangeDate = (newDate) => {
    if (moment(newDate).isValid()) {
      setValidTo(moment(newDate));
    }
  };

  const onSubmit = () => {
    const localChanges = selectedRecords.map((item) => (
      moment(item.validFrom).isSameOrBefore(moment().startOf('day'))
        ? { ...item, validTo: validTo.format(DateFormats.date) } : { ...item, willDeleted: true }));
    confirmDeactivate(localChanges);
    closeDialog(true);
  };

  const onCancel = () => {
    closeDialog(false);
    setValidTo(moment());
  };

  const getValidationError = () => `There is an active mapping for the ${overlappingDenest} with overlapping validity.`;

  return (
    <Dialog onClose={onCancel} aria-labelledby="simple-dialog-title" open={openDialog}>
      <StyledModalForm>
        <DialogTitle id="simple-dialog-title">Deactivate on</DialogTitle>
        <table className="form-details">
          <tbody>
            <tr>
              <td className="form-input-fields">
                <Label>Valid to*</Label>
              </td>
              <td className="form-input-fields">
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                  <Grid container>
                    <KeyboardDatePicker
                      disablePast
                      minDate={moment()}
                      disableToolbar
                      autoOk
                      format="dd-MM-yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      value={validTo}
                      onChange={onChangeDate}
                    />
                  </Grid>
                </MuiPickersUtilsProvider>
              </td>
            </tr>

          </tbody>
        </table>
        {overlappingDenest && !isFormValid && (
          <div className="validation-message">
            <span>
              {getValidationError()}
            </span>
          </div>
        )}
        {!!selectedRecords.find((item) => moment(item.validFrom).isAfter(moment().startOf('day'))) && (
          <div className="info-message">
            <span>
              {formatMessage('en', 'impcmapping.deactive.future.records.info.message')}
            </span>
          </div>
        )}
        <div className="after-box">
          <Button className="cancel" onClick={onCancel}>Cancel</Button>
          <Button
            className={`primary-button-${!isFormValid} action`}
            disabled={!isFormValid}
            onClick={onSubmit}
          >
            Submit
          </Button>
        </div>
      </StyledModalForm>
    </Dialog>

  );
}

DeactivateDenestImpcMappingDialog.propTypes = {
  closeDialog: PropTypes.func,
  openDialog: PropTypes.bool,
  selectedRecords: PropTypes.arrayOf(PropTypes.object),
  confirmDeactivate: PropTypes.func,
  tableData: PropTypes.arrayOf(PropTypes.object),
};

DeactivateDenestImpcMappingDialog.defaultProps = {
  openDialog: false,
  selectedRecords: [],
};

export default DeactivateDenestImpcMappingDialog;
